import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import jwtAxios, { setAuthToken } from "./index";
import { AuthUser } from "../../../../types/models/AuthUser";
import {
  fetchError,
  fetchStart,
  fetchSuccess,
} from "../../../../redux/actions";
import { useNavigate } from "react-router-dom";

interface JWTAuthContextProps {
  user: AuthUser | null | undefined;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface SignUpProps {
  name: string;
  email: string;
  password: string;
}

interface SignInProps {
  email: string;
  password: string;
}

interface JWTAuthActionsProps {
  signUpUser: (data: SignUpProps) => void;
  signInUser: (data: SignInProps) => void;
  logout: () => void;
}

const JWTAuthContext = createContext<JWTAuthContextProps>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
});

const JWTAuthActionsContext = createContext<JWTAuthActionsProps>({
  signUpUser: () => {},
  signInUser: () => {},
  logout: () => {},
});

export const useJWTAuth = () => useContext(JWTAuthContext);
export const useJWTAuthActions = () => useContext(JWTAuthActionsContext);

interface JWTAuthAuthProviderProps {
  children: ReactNode;
}

const JWTAuthAuthProvider: React.FC<JWTAuthAuthProviderProps> = ({
  children,
}) => {
  const [jwtAuthData, setJWTAuthData] = useState<JWTAuthContextProps>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  useEffect(() => {
    const getAuthUser = async () => {
      const token = localStorage.getItem("token");
      const storedUser = localStorage.getItem("userInfo");

      if (jwtAuthData.isAuthenticated || !token || !storedUser) {
        setJWTAuthData((prev) => ({
          ...prev,
          isLoading: false,
        }));
        return;
      }

      setAuthToken(token);

      try {
        const parsedUser: AuthUser = JSON.parse(storedUser);
        const userId = parsedUser.id;

        const response = await jwtAxios.get(`/users/${userId}`);
        const user = response.data?.data || parsedUser;

        localStorage.setItem("userInfo", JSON.stringify(user));

        setJWTAuthData({
          user,
          isAuthenticated: true,
          isLoading: false,
        });
      } catch (error) {
        console.error("Error fetching user by ID:", error);
        setJWTAuthData({
          user: undefined,
          isAuthenticated: false,
          isLoading: false,
        });
      }
    };

    getAuthUser();
  }, [jwtAuthData.isAuthenticated]);

 const signInUser = async ({ email, password }: SignInProps) => {
  dispatch(fetchStart());
  try {
    const { data } = await jwtAxios.post("/users/login", { email, password });

    if (data.data && data.data.accessToken) {
      const token = data.data.accessToken;
      const loginUser = data.data;
      const roleId = loginUser.roleId;

      localStorage.setItem("token", token);
      setAuthToken(token);

      const userId = loginUser.id;
      const userRes = await jwtAxios.get(`/users/${userId}`);
      const fullUser = userRes.data?.data || loginUser;

      const modulesRes = await jwtAxios.get(`/roleModule/byRoleId/${roleId}`);
      const modules = modulesRes.data?.data || [];

      localStorage.setItem("userModules", JSON.stringify(modules));
      localStorage.setItem("userInfo", JSON.stringify(fullUser));

      const allowedModuleNames = modules.map((mod: any) => mod.module_name);

      let initialUrl = "/dashboard"; 

      if (allowedModuleNames.includes("Dashboard")) {
        initialUrl = "/dashboard";
      } else if (allowedModuleNames.includes("Manage Users")) {
        initialUrl = "/master/manageUser";
      } else if (allowedModuleNames.includes("Manage Banners")) {
        initialUrl = "/master/manageBanner";
      } else if (allowedModuleNames.includes("Add/Edit Polygon")) {
        initialUrl = "/master/manageViewPolygon";
      } else if (allowedModuleNames.includes("View Plan")) {
        initialUrl = "/planMaster/ViewPlan";
      } else if (allowedModuleNames.includes("Manage CC Lead Page")) {
        initialUrl = "/customerCare/list";
      } else if (allowedModuleNames.includes("Add CC Lead")) {
        initialUrl = "/customerCare/CustomerPageCreate";
      }

      localStorage.setItem("initialUrl", initialUrl);

      setJWTAuthData({
        user: fullUser,
        isAuthenticated: true,
        isLoading: false,
      });

      dispatch(fetchSuccess());


      setTimeout(() => {
        window.location.href = initialUrl;
      }, 0.00001);

    } else {
      throw new Error("Invalid login response");
    }
  } catch (error) {
    console.error("Login error:", error);
    setJWTAuthData({
      ...jwtAuthData,
      isAuthenticated: false,
      isLoading: false,
    });
    dispatch(fetchError("Something went wrong"));
  }
};




  const signUpUser = async ({ name, email, password }: SignUpProps) => {
    dispatch(fetchStart());
    try {
      const { data } = await jwtAxios.post("/users", { name, email, password });

      if (data.token) {
        localStorage.setItem("token", data.token);
        setAuthToken(data.token);

        const loginRes = await jwtAxios.post("/users/login", { email, password });
        const loginUser = loginRes.data?.data;
        const userId = loginUser?.id;

        if (!userId) throw new Error("Missing user ID after signup");

        const userRes = await jwtAxios.get(`/users/${userId}`);
        const fullUser = userRes.data?.data || loginUser;

        localStorage.setItem("userInfo", JSON.stringify(fullUser));

        setJWTAuthData({
          user: fullUser,
          isAuthenticated: true,
          isLoading: false,
        });

        dispatch(fetchSuccess());
      } else {
        throw new Error("No token received on signup");
      }
    } catch (error) {
      console.error("Signup error:", error);
      setJWTAuthData({
        ...jwtAuthData,
        isAuthenticated: false,
        isLoading: false,
      });
      dispatch(fetchError("Something went wrong"));
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userInfo");
    localStorage.removeItem("userModules");  
    localStorage.removeItem("initialUrl");
    setAuthToken();
    setJWTAuthData({
      user: null,
      isAuthenticated: false,
      isLoading: false,
    });
  };

  return (
    <JWTAuthContext.Provider value={jwtAuthData}>
      <JWTAuthActionsContext.Provider
        value={{
          signUpUser,
          signInUser,
          logout,
        }}
      >
        {children}
      </JWTAuthActionsContext.Provider>
    </JWTAuthContext.Provider>
  );
};

export default JWTAuthAuthProvider;
